'use client';

import Link from 'next/link';
import { cn } from '../../lib/utils';
import { Icon } from '@iconify/react';
import gsap from 'gsap';
import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Call-to-action section component
 * Encourages visitors to get in touch for collaboration
 * Enhanced with professional design and animations
 * Responsive design for all screen sizes
 * Uses Iconify for high-quality, scalable icons
 */
export function CTASection() {
  const { t } = useTranslation();

  const sectionRef = useRef<HTMLElement>(null);
  const cardRef = useRef<HTMLDivElement>(null);
  const dec1Ref = useRef<HTMLDivElement>(null);
  const dec2Ref = useRef<HTMLDivElement>(null);
  const contentRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const tl = gsap.timeline();

    if (sectionRef.current) gsap.set(sectionRef.current, { opacity: 0 });

    tl.to(sectionRef.current, { opacity: 1, duration: 0.6 });

    if (cardRef.current) {
      tl.fromTo(cardRef.current, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.6, ease: "power2.out" }, "-=0.4");
    }

    if (contentRef.current) {
      tl.fromTo(contentRef.current.children,
        { y: 20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5, stagger: 0.15, ease: "power2.out" },
        "-=0.4"
      );
    }

    // Use CSS animations instead of GSAP for infinite loops to free the main thread
    if (dec1Ref.current) {
      dec1Ref.current.style.animation = "dec1Float 4s ease-in-out infinite alternate";
    }
    if (dec2Ref.current) {
      dec2Ref.current.style.animation = "dec2Float 3s ease-in-out infinite alternate";
    }
  }, []);

  return (
    <section
      ref={sectionRef}
      className="mb-12 sm:mb-16"
    >
      <div
        ref={cardRef}
        className="bg-gradient-to-r from-slate-50 to-slate-100 dark:from-card dark:to-card p-6 sm:p-8 rounded-xl border border-slate-200 dark:border-slate-700/60 shadow-sm relative overflow-hidden"
      >
        {/* Decorative elements */}
        <div
          ref={dec1Ref}
          className="absolute top-0 right-0 w-40 h-40 bg-gradient-to-br from-slate-300/20 to-slate-500/20 rounded-bl-full"
        />
        <div
          ref={dec2Ref}
          className="absolute -bottom-4 -left-4 w-20 h-20 bg-gradient-to-tr from-slate-400/20 to-slate-600/20 rounded-full"
        />

        <div ref={contentRef} className="max-w-3xl mx-auto relative z-10">
          <div
            className="flex items-center gap-3 mb-5"
          >
            <div className="h-10 w-10 rounded-full bg-slate-200 dark:bg-slate-700 flex items-center justify-center">
              <Icon icon="carbon:collaborate" className="h-5 w-5 text-slate-900 dark:text-foreground" />
            </div>
            <h2 className="text-xl sm:text-2xl font-bold text-foreground">{t('CTA.title')}</h2>
          </div>

          <p
            className="text-base sm:text-lg text-foreground mb-4 leading-relaxed max-w-2xl"
          >
            {t('CTA.description')}
          </p>

          <p className="text-xs text-slate-500 dark:text-muted-foreground mb-8 italic">
            {t('CTA.microcopy')}
          </p>

          <div
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link
              href="/contact"
              prefetch={false}
              className={cn(
                'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-bold',
                'bg-[#181A1D] text-white hover:bg-slate-800 dark:bg-white dark:text-[#181A1D] dark:hover:bg-slate-100',
                'transition-all duration-300 shadow-lg shadow-black/5 dark:shadow-white/5 hover:scale-105 active:scale-95'
              )}
            >
              <Icon icon="solar:calendar-linear" className="h-5 w-5" />
              {t('CTA.primary')}
            </Link>

            <Link
              href="/projects"
              prefetch={false}
              className={cn(
                'inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full text-sm font-bold',
                'bg-white text-[#181A1D] hover:bg-slate-50 dark:bg-slate-800 dark:text-white dark:hover:bg-slate-700',
                'border border-slate-200 dark:border-slate-700 transition-all duration-300 shadow-sm hover:shadow hover:scale-105 active:scale-95',
                'group'
              )}
            >
              {t('CTA.secondary')}
              <Icon
                icon="carbon:arrow-right"
                className="h-4 w-4 group-hover:translate-x-1 transition-transform"
              />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
} 